/**
 * נייר השלמות — "why is my document blocked", as a first-class screen with its own address.
 * 09 decision 7, §5.7b.
 *
 * A blocked document is NOT an error (B1). It is a checklist a non-technical customer can finish
 * without calling the office — which is the whole reason this is a screen and not a toast.
 * B4: the draft is always producible even when the final is blocked; a CPA needs to see the shape
 * of the report before it is complete.
 */

import { useEffect, useState } from 'react';
import { AppShell } from '../shell/AppShell';
import { useDeclareScreenContext, useWorkContext } from '../shell/ContextProvider';
import { BlockedReasons } from '../components/BlockedReasons';
import { Modal } from '../components/Modal';
import { StateBlock } from '../components/StateBlock';
import { fixtureBlockedDocument, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';
import type { BlockedDocument } from '../api/contract';

export interface BlockedDocumentScreenProps {
  readonly companyUid: string;
  readonly documentUid: string;
}

export function BlockedDocumentScreen({
  companyUid,
  documentUid,
}: BlockedDocumentScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const { setCompanyByUid, setEntityLabel } = useDeclareScreenContext();
  const [markingUid, setMarkingUid] = useState<string | null>(null);

  useEffect(() => {
    setCompanyByUid(companyUid);
    setEntityLabel('נייר השלמות');
  }, [companyUid, setCompanyByUid, setEntityLabel]);

  // Development fixture, clearly labelled. With the API running this is a fetch.
  const doc: BlockedDocument | null =
    fixturesEnabled() && documentUid === fixtureBlockedDocument.documentUid
      ? fixtureBlockedDocument
      : null;

  const back = {
    labelHe: ctx.company?.legalName ?? 'דף הבית של החברה',
    href: routes.companyHome(companyUid),
  };

  if (doc === null) {
    return (
      <AppShell titleHe="נייר השלמות" backToParent={back}>
        <StateBlock
          titleHe="הדף שחיפשת לא קיים"
          bodyHe="המסמך שביקשת אינו קיים או שאינו זמין לך."
          nextStep={<a className="btn" href={back.href}>חזרה ל{back.labelHe}</a>}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      titleHe={`נייר השלמות — ${doc.companyLegalName} · ${doc.periodDisplayHe}`}
      backToParent={back}
    >
      <BlockedReasons
        blockers={doc.blockers}
        documentKindHe={doc.documentKindHe}
        onMarkNotRelevant={(uid) => setMarkingUid(uid)}
      />

      <div className="actions-row">
        {/* B4 — the draft is producible even while the final is blocked. */}
        {doc.draftAvailable ? (
          <button type="button" className="btn">
            הפקת טיוטה
          </button>
        ) : null}
        {/* A verb. The result is a job with visible state (§5.7), never a silent download. */}
        <button type="button" className="btn btn--primary">
          ניסיון הפקה מחדש
        </button>
      </div>

      {markingUid === null ? null : (
        // 09 §2.3 row 5 class: one decision, two fields, reversible by re-opening. A modal qualifies.
        // B5 — it records WHO marked it and WHY; suppressing a blocker silently would defeat the control.
        <Modal
          intentHe="סימון חסם כלא רלוונטי"
          blastRadiusHe={`הסימון יירשם על שמך עבור ${doc.companyLegalName} · ${doc.periodDisplayHe} ויופיע בגיליון הממצאים.`}
          fields={2}
          confirmLabelHe="סימון כלא רלוונטי"
          onConfirm={() => setMarkingUid(null)}
          onClose={() => setMarkingUid(null)}
        >
          <label className="field">
            <span className="field__label">סיבה</span>
            <textarea className="field__input" rows={3} />
          </label>
          <label className="field">
            <span className="field__label">בתוקף עד</span>
            <input className="field__input" type="date" dir="ltr" />
          </label>
        </Modal>
      )}
    </AppShell>
  );
}
